import { useState, useEffect, useRef, useMemo } from 'react';
import { apiGet, apiPost, getLocalDate, getLocalTime, computeStreakFromEntries } from '../appUtils';
import { toast } from 'sonner';
import { type PainArea, type Screen } from '../appTypes';
import { type PainIntensity } from '../constants/painConstants';
import { type PainSiteResult } from '../CareWeaveBodyMap';
import { type EmotionZoneResult } from '../CareWeaveEmotionMap';
import { SK_CHECKINS, SK_LAST_CRISIS_DATE, SK_ENABLED_CHECKIN_QUESTIONS, SK_SEEN_CHECKIN_INTRO, checkinDraftKey } from '../constants/storageKeys';
import { getGuideCategory, getGuideMessage } from '../components/guideCatalogue';
import { getDemoCheckins } from '../demoData';
import type { UserProfile } from '../auth';

// Timing constants
const AFFIRMATION_DISPLAY_MS = 6000;

/** Stable ref wrapper so the hook can call showScreen without depending on its definition order */
function useLatest<T>(value: T) {
  const ref = useRef(value);
  ref.current = value;
  return ref;
}

export type DailyCheckinSummary = {
  painLevel: number | null;
  moodLevel: number | null;
  energyLevel: number | null;
  painAreas: PainArea[];
  copingStrategies: string[];
  treatment: string | null;
  stressLevel: number | null;
  stressDescriptors: string[] | null;
  feelings: string[] | null;
  fatigueLevel: number | null;
  fatigueType: string[] | null;
  worryLevel: number | null;
  feelingBlueLevel: number | null;
  sleepQuality: number | null;
  bodySensations: string[] | null;
  mood: string | null;
  skippedSteps: number[];
};

export type CheckinEntry = {
  timestamp: string;
  isVOC: boolean;
  copingStrategies: string[];
  painAreas?: PainArea[];
  moodLevel?: number;
  energyLevel?: number;
  painLevel?: number;
  stressLevel?: number;
  fatigueLevel?: number;
  sleepQuality?: number;
  worryLevel?: number;
  feelingBlueLevel?: number;
};

interface UseCheckinParams {
  authToken: string | null;
  currentUser: UserProfile | null;
  setCurrentUser: React.Dispatch<React.SetStateAction<UserProfile | null>>;
  earnXP: (amount: number) => void;
  earnXPOnce: (key: string, amount: number) => void;
  showScreen: (s: Screen) => void;  // stable or ref-wrapped; may be reassigned after first render
  screen: Screen;
  essentialsMode: boolean;
  demoActive: boolean;
  containerRef: React.RefObject<HTMLElement | null>;
  enabledCheckinQuestions: Set<number>;
  setEnabledCheckinQuestions: React.Dispatch<React.SetStateAction<Set<number>>>;
}

export function useCheckin({
  authToken,
  currentUser,
  setCurrentUser,
  earnXP,
  earnXPOnce,
  showScreen,
  screen,
  essentialsMode,
  demoActive,
  containerRef,
  enabledCheckinQuestions,
  setEnabledCheckinQuestions,
}: UseCheckinParams) {
  // Keep a ref so event-handler closures always see the latest showScreen,
  // regardless of when the hook is called relative to showScreen's definition.
  const showScreenRef = useLatest(showScreen);
  // ── Daily check-in form state ─────────────────────────────────────
  const [checkinStep, setCheckinStep] = useState(1);
  const [checkinDone, setCheckinDone] = useState(false);
  const [watersCheck, setWatersCheck] = useState<string | null>(null);
  const [selectedBodySensations, setSelectedBodySensations] = useState<string[]>([]);
  const [stressValue, setStressValue] = useState<number | null>(null);
  const [selectedStressDescriptors, setSelectedStressDescriptors] = useState<string[]>([]);
  const [selectedFeelings, setSelectedFeelings] = useState<string[]>([]);
  const [fatigueValue, setFatigueValue] = useState<number | null>(null);
  const [selectedFatigueType, setSelectedFatigueType] = useState<string[]>([]);
  const [worryValue, setWorryValue] = useState<number | null>(null);
  const [selectedWorryChips, setSelectedWorryChips] = useState<string[]>([]);
  const [feelingBlueValue, setFeelingBlueValue] = useState<number | null>(null);
  const [selectedBlueChips, setSelectedBlueChips] = useState<string[]>([]);
  const [selectedSleepChips, setSelectedSleepChips] = useState<string[]>([]);
  const [selectedMoodChip, setSelectedMoodChip] = useState<string | null>(null);
  const [customCheckinChips, setCustomCheckinChips] = useState<Record<string, string[]>>({});
  const [skippedCheckinSteps, setSkippedCheckinSteps] = useState<number[]>([]);
  const [showCrisisReturnNudge, setShowCrisisReturnNudge] = useState(false);
  const [showAnchorNudge, setShowAnchorNudge] = useState(false);

  const [checkinSummary, setCheckinSummary] = useState<DailyCheckinSummary | null>(null);
  const [guideAffirmation, setGuideAffirmation] = useState<string | null>(null);
  const [painValue, setPainValue] = useState<number | null>(null);
  const [moodValue, setMoodValue] = useState<number | null>(null);
  const [energyValue, setEnergyValue] = useState<number | null>(null);
  const [checkinPainAreas, setCheckinPainAreas] = useState<Record<string, PainIntensity>>({});
  const [bodySpotWords, setBodySpotWords] = useState<Record<string, string[]>>({});
  const [bodySpotFreeText, setBodySpotFreeText] = useState<Record<string, string>>({});
  const [activeBodySpot, setActiveBodySpot] = useState<string | null>(null);
  const [painMapResult, setPainMapResult] = useState<{ sites: PainSiteResult[]; note: string | null } | null>(null);
  const [emotionMapResult, setEmotionMapResult] = useState<{ zones: EmotionZoneResult[]; note: string | null } | null>(null);
  const [mindBodyStressValue, setMindBodyStressValue] = useState<number | null>(null);
  const [groundingShown, setGroundingShown] = useState(false);
  const [mindBodyChips, setMindBodyChips] = useState<string[]>([]);
  const [sleepFatigueValue, setSleepFatigueValue] = useState<number | null>(null);
  const [selectedCopingStrategies, setSelectedCopingStrategies] = useState<string[]>([]);
  const [customCopingStrategies, setCustomCopingStrategies] = useState<string[]>([]);
  const [customCopingInput, setCustomCopingInput] = useState('');
  const [showCustomCopingInput, setShowCustomCopingInput] = useState(false);
  const [selectedTreatment, setSelectedTreatment] = useState<string | null>(null);
  const [selectedProtectiveFactors, setSelectedProtectiveFactors] = useState<string[]>([]);
  const [checkinIsVOC, setCheckinIsVOC] = useState<boolean | null>(null);

  // ── Check-in entries & persistence ────────────────────────────────
  const [_rawCheckinEntries, setCheckinEntries] = useState<CheckinEntry[]>(() => {
    try { return JSON.parse(localStorage.getItem(SK_CHECKINS) || '[]'); } catch { return []; }
  });
  const [savedCheckins, setSavedCheckins] = useState<Array<{ timestamp: string; copingStrategies: string[]; painAreas?: PainArea[] }>>([]);
  const [lastCrisisDate, setLastCrisisDate] = useState<string>(() => {
    const stored = localStorage.getItem(SK_LAST_CRISIS_DATE);
    if (stored) return stored;
    const today = getLocalDate();
    localStorage.setItem(SK_LAST_CRISIS_DATE, today);
    return today;
  });
  const [totalCheckins, setTotalCheckins] = useState(7);

  // Demo data merge layer
  const checkinEntries = useMemo(
    () => demoActive ? [..._rawCheckinEntries, ...getDemoCheckins()] : _rawCheckinEntries,
    [_rawCheckinEntries, demoActive],
  );

  const streak = computeStreakFromEntries(checkinEntries);

  // ── Check-in intro & draft state ──────────────────────────────────
  const [showCheckinIntro, setShowCheckinIntro] = useState(false);
  const [showEDCheckinIntro, setShowEDCheckinIntro] = useState(false);
  const [checkinDraftActive, setCheckinDraftActive] = useState(false);
  const [checkinReturnScreen, setCheckinReturnScreen] = useState<Screen>('home');

  // ── Check-in scroll state ─────────────────────────────────────────
  const [checkinScrollTop, setCheckinScrollTop] = useState(false);
  const [checkinScrollBottom, setCheckinScrollBottom] = useState(false);
  const checkinScreenRef = useRef<HTMLDivElement>(null);

  // enabledCheckinQuestions + setEnabledCheckinQuestions are now passed in as params

  function toggleCheckinQuestion(questionNum: number) {
    if (essentialsMode) return;
    const newSet = new Set(enabledCheckinQuestions);
    if (newSet.has(questionNum)) {
      newSet.delete(questionNum);
    } else {
      newSet.add(questionNum);
    }
    setEnabledCheckinQuestions(newSet);
    localStorage.setItem(SK_ENABLED_CHECKIN_QUESTIONS, JSON.stringify([...newSet].sort((a,b) => a-b)));
  }

  // ── Body map metrics (computed) ───────────────────────────────────
  const bodyMapMetrics = (() => {
    const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const recentCheckins = savedCheckins.filter(c => new Date(c.timestamp).getTime() >= oneWeekAgo);

    let topStrategy: string | null = null;
    if (recentCheckins.length > 0) {
      const counts: Record<string, number> = {};
      for (const c of recentCheckins) {
        for (const s of c.copingStrategies) {
          if (s !== 'Nothing yet') counts[s] = (counts[s] || 0) + 1;
        }
      }
      const entries = Object.entries(counts);
      if (entries.length > 0) {
        entries.sort((a, b) => b[1] - a[1]);
        topStrategy = entries[0][0];
      }
    }

    let peakPeriod: string | null = null;
    if (savedCheckins.length > 0) {
      const hourBuckets: Record<string, number> = { mornings: 0, afternoons: 0, evenings: 0, nights: 0 };
      for (const c of savedCheckins) {
        const h = new Date(c.timestamp).getHours();
        if (h >= 5 && h < 12) hourBuckets.mornings++;
        else if (h >= 12 && h < 17) hourBuckets.afternoons++;
        else if (h >= 17 && h < 21) hourBuckets.evenings++;
        else hourBuckets.nights++;
      }
      const peak = Object.entries(hourBuckets).sort((a, b) => b[1] - a[1])[0];
      if (peak[1] > 0) peakPeriod = peak[0];
    }

    return { topStrategy, peakPeriod };
  })();

  // ── Effects ───────────────────────────────────────────────────────

  // Scroll management for check-in screen
  useEffect(() => {
    if (screen !== 'checkin') {
      setCheckinScrollTop(false);
      setCheckinScrollBottom(false);
      return;
    }
    const el = containerRef.current;
    if (!el) return;
    const checkScroll = () => {
      setCheckinScrollTop(el.scrollTop >= 40);
      setCheckinScrollBottom(el.scrollTop + el.clientHeight < el.scrollHeight - 40);
    };
    el.scrollTo({ top: 0 });
    setTimeout(checkScroll, 50);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, checkinStep]);

  // Guide affirmation auto-dismiss
  useEffect(() => {
    if (!guideAffirmation) return;
    const timer = setTimeout(() => setGuideAffirmation(null), AFFIRMATION_DISPLAY_MS);
    return () => clearTimeout(timer);
  }, [guideAffirmation]);

  // Detect existing draft on mount
  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    const key = checkinDraftKey(today);
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        setCheckinDraftActive(true);
      }
    } catch { /* localStorage may be unavailable */ }
  }, []);

  // Auto-save draft while on checkin screen
  useEffect(() => {
    if (screen === 'checkin' && !checkinSummary) {
      saveCheckinDraft();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, checkinStep, painValue, moodValue, energyValue, mindBodyStressValue, sleepFatigueValue, checkinPainAreas, selectedCopingStrategies, selectedTreatment, checkinIsVOC, mindBodyChips, painMapResult, emotionMapResult]);

  // ── API functions ─────────────────────────────────────────────────

  async function loadCheckins() {
    type DBCheckin = {
      id: number;
      date: string;
      time?: string | null;
      painLevel: number;
      moodLevel?: number | null;
      energyLevel?: number | null;
      painAreas?: Array<{ area: string; intensity: number }> | null;
      treatment?: string | null;
      isVOC?: boolean | null;
      copingStrategies?: string[] | null;
      createdAt?: string | null;
    };
    const rows = await apiGet<DBCheckin[]>('/daily-checkins', authToken);
    if (rows && rows.length > 0) {
      const mapped = rows.map(r => ({
        timestamp: r.time ? `${r.date}T${r.time}:00` : (r.createdAt ?? `${r.date}T00:00:00`),
        isVOC: r.isVOC ?? false,
        copingStrategies: r.copingStrategies ?? [],
        painAreas: (r.painAreas ?? []) as Array<{ area: string; intensity: PainIntensity }>,
        moodLevel: r.moodLevel ?? undefined,
        energyLevel: r.energyLevel ?? undefined,
        painLevel: r.painLevel,
      }));
      setCheckinEntries(mapped);
      try { localStorage.setItem(SK_CHECKINS, JSON.stringify(mapped)); } catch { /* localStorage may be unavailable */ }
    }
  }

  // ── Draft management ──────────────────────────────────────────────

  function saveCheckinDraft() {
    try {
      const draft = {
        checkinStep, painValue, moodValue, energyValue, mindBodyStressValue,
        sleepFatigueValue, checkinPainAreas, selectedCopingStrategies,
        selectedTreatment, checkinIsVOC, mindBodyChips, painMapResult, emotionMapResult,
      };
      localStorage.setItem(checkinDraftKey(new Date().toISOString().slice(0, 10)), JSON.stringify(draft));
    } catch { /* localStorage may be unavailable */ }
  }

  function clearCheckinDraft() {
    try { localStorage.removeItem(checkinDraftKey(new Date().toISOString().slice(0, 10))); } catch { /* localStorage may be unavailable */ }
    setCheckinDraftActive(false);
  }

  function exitCheckinWithSave() {
    saveCheckinDraft();
    setCheckinDraftActive(true);
    showScreenRef.current('home');
  }

  // ── Navigation / step functions ───────────────────────────────────

  function skipCheckinStep() {
    if (essentialsMode) return;
    setSkippedCheckinSteps(prev => prev.includes(checkinStep) ? prev : [...prev, checkinStep]);
    nextCheckinStep();
  }

  function nextCheckinStep() {
    let nextStep = checkinStep + 1;
    while (nextStep <= 10 && !enabledCheckinQuestions.has(nextStep)) {
      nextStep++;
    }
    if (nextStep <= 10) {
      setCheckinStep(nextStep);
    } else {
      submitCheckin();
    }
  }

  function prevCheckinStep() {
    let prevStep = checkinStep - 1;
    while (prevStep >= 1 && !enabledCheckinQuestions.has(prevStep)) {
      prevStep--;
    }
    if (prevStep >= 1) setCheckinStep(prevStep);
  }

  function toggleCopingStrategy(strategy: string) {
    setSelectedCopingStrategies(prev =>
      prev.includes(strategy) ? prev.filter(s => s !== strategy) : [...prev, strategy]
    );
  }

  function toggleProtectiveFactor(factor: string) {
    setSelectedProtectiveFactors(prev =>
      prev.includes(factor) ? prev.filter(f => f !== factor) : [...prev, factor]
    );
  }

  // ── Start check-in ────────────────────────────────────────────────

  function doStartCheckin(resumeDraft = false) {
    const today = new Date().toISOString().slice(0, 10);
    const draftKey = checkinDraftKey(today);
    let draftRestored = false;
    if (resumeDraft) {
      try {
        const raw = localStorage.getItem(draftKey);
        if (raw) {
          const parsed = JSON.parse(raw);
          // NEW-010: validate draft schema before restoring
          if (typeof parsed !== 'object' || parsed === null || typeof parsed.checkinStep !== 'number') {
            localStorage.removeItem(draftKey);
          } else {
            const typedDraft = parsed as {
              checkinStep?: number; painValue?: number; moodValue?: number;
              energyValue?: number; mindBodyStressValue?: number; sleepFatigueValue?: number;
              checkinPainAreas?: Record<string, PainIntensity>;
              selectedCopingStrategies?: string[]; selectedTreatment?: string | null;
              checkinIsVOC?: boolean | null; mindBodyChips?: string[];
            };
            if (typedDraft.checkinStep) setCheckinStep(typedDraft.checkinStep);
            if (typedDraft.painValue !== undefined) setPainValue(typedDraft.painValue);
            if (typedDraft.moodValue !== undefined) setMoodValue(typedDraft.moodValue);
            if (typedDraft.energyValue !== undefined) setEnergyValue(typedDraft.energyValue);
            if (typedDraft.mindBodyStressValue !== undefined) setMindBodyStressValue(typedDraft.mindBodyStressValue);
            if (typedDraft.sleepFatigueValue !== undefined) setSleepFatigueValue(typedDraft.sleepFatigueValue);
            if (typedDraft.checkinPainAreas) setCheckinPainAreas(typedDraft.checkinPainAreas);
            if (typedDraft.selectedCopingStrategies) setSelectedCopingStrategies(typedDraft.selectedCopingStrategies);
            if (typedDraft.selectedTreatment !== undefined) setSelectedTreatment(typedDraft.selectedTreatment);
            if (typedDraft.checkinIsVOC !== undefined) setCheckinIsVOC(typedDraft.checkinIsVOC);
            if (typedDraft.mindBodyChips) setMindBodyChips(typedDraft.mindBodyChips);
            if ((typedDraft as any).painMapResult) setPainMapResult((typedDraft as any).painMapResult);
            if ((typedDraft as any).emotionMapResult) setEmotionMapResult((typedDraft as any).emotionMapResult);
            draftRestored = true;
          }
        }
      } catch { /* error handled silently */ }
    }
    if (!draftRestored) {
      setCheckinStep(1);
      const previousAreas: Record<string, PainIntensity> = {};
      if (checkinEntries.length > 0) {
        const last = checkinEntries[checkinEntries.length - 1];
        if (last.painAreas) {
          for (const pa of last.painAreas) {
            previousAreas[pa.area] = pa.intensity;
          }
        }
      }
      setCheckinPainAreas(previousAreas);
      setSelectedCopingStrategies([]);
      setCustomCopingStrategies([]);
      setCustomCopingInput('');
      setShowCustomCopingInput(false);
      setSelectedTreatment(null);
      setSelectedProtectiveFactors([]);
      setPainValue(null);
      setMoodValue(null);
      setEnergyValue(null);
      setSleepFatigueValue(null);
      setCheckinIsVOC(null);
      setWatersCheck(null);
      setSelectedBodySensations([]);
      setStressValue(null);
      setFatigueValue(null);
      setSelectedFatigueType([]);
      setSelectedMoodChip(null);
      setCustomCheckinChips(currentUser?.customChips ?? {});
      setSkippedCheckinSteps([]);
      setPainMapResult(null);
      setEmotionMapResult(null);
      setShowCrisisReturnNudge(false);
      setShowAnchorNudge(false);
    }
    setCheckinSummary(null);
    showScreenRef.current('checkin');
  }

  function startCheckin(resume = false) {
    if (resume) {
      doStartCheckin(true);
      return;
    }
    const checkinIntroSeen = localStorage.getItem(SK_SEEN_CHECKIN_INTRO) === 'true';
    if (!checkinIntroSeen) {
      showScreenRef.current('checkin');
      setShowCheckinIntro(true);
    } else {
      doStartCheckin();
    }
  }

  // ── Submit daily check-in ─────────────────────────────────────────

  async function submitCheckin(skipScreenTransition = false) {
    const painAreas = painMapResult
      ? painMapResult.sites.map(s => ({ area: s.name, intensity: Math.min(s.severity, 3) as 1 | 2 | 3 }))
      : Object.entries(checkinPainAreas).map(([area, intensity]) => ({ area, intensity }));
    const dateStr = getLocalDate();
    const timeStr = getLocalTime();
    const timestamp = new Date().toISOString();
    const activeGuideId = currentUser?.avatarId || undefined;
    const submitOk = await apiPost('/daily-checkins', {
      date: dateStr,
      time: timeStr,
      painLevel: painValue,
      moodLevel: moodValue,
      energyLevel: energyValue,
      painAreas,
      copingStrategies: selectedCopingStrategies,
      treatment: selectedTreatment,
      isVOC: checkinIsVOC === true,
      activeGuideId,
      watersCheck: watersCheck ?? null,
      bodySensations: selectedBodySensations.length > 0 ? selectedBodySensations : null,
      stressLevel: stressValue,
      stressDescriptors: selectedStressDescriptors.length > 0 ? selectedStressDescriptors : null,
      feelings: selectedFeelings.length > 0 ? selectedFeelings : null,
      fatigueLevel: fatigueValue,
      fatigueType: selectedFatigueType.length > 0 ? selectedFatigueType : null,
      worryLevel: worryValue,
      feelingBlueLevel: feelingBlueValue,
      sleepQuality: sleepFatigueValue,
      mood: selectedMoodChip ?? null,
      customDescriptors: Object.keys(customCheckinChips).length > 0 ? customCheckinChips : null,
      skippedSteps: skippedCheckinSteps.length > 0 ? skippedCheckinSteps : null,
      painSites: painMapResult ?? null,
      emotionZones: emotionMapResult ?? null,
    }, authToken);
    if (!submitOk) {
      toast.error("Your check-in didn't save. Try again — your draft is still on this device.");
      return;
    }
    if (activeGuideId) {
      setCurrentUser(prev => {
        if (!prev) return prev;
        const progress = { ...(prev.guideBondProgress ?? {}) };
        progress[activeGuideId] = (progress[activeGuideId] ?? 0) + 1;
        return { ...prev, guideBondProgress: progress };
      });
    }
    const newCheckinEntry: CheckinEntry = { timestamp, isVOC: checkinIsVOC === true, copingStrategies: selectedCopingStrategies, painAreas, moodLevel: moodValue ?? undefined, energyLevel: energyValue ?? undefined, painLevel: painValue ?? undefined, stressLevel: stressValue ?? undefined, fatigueLevel: fatigueValue ?? undefined, sleepQuality: sleepFatigueValue ?? undefined, worryLevel: worryValue ?? undefined, feelingBlueLevel: feelingBlueValue ?? undefined };
    const updatedEntries = [...checkinEntries, newCheckinEntry];
    setCheckinEntries(updatedEntries);
    localStorage.setItem(SK_CHECKINS, JSON.stringify(updatedEntries));
    if (checkinIsVOC === true) {
      setLastCrisisDate(dateStr);
      localStorage.setItem(SK_LAST_CRISIS_DATE, dateStr);
    }
    setSavedCheckins(prev => [...prev, { timestamp, copingStrategies: selectedCopingStrategies, painAreas }]);
    earnXP(50);
    earnXPOnce('first_checkin', 30);
    setTotalCheckins(prev => prev + 1);
    setCheckinDone(true);
    setCheckinSummary({
      painLevel: painValue,
      moodLevel: moodValue,
      energyLevel: energyValue,
      painAreas,
      copingStrategies: selectedCopingStrategies,
      treatment: selectedTreatment,
      stressLevel: stressValue,
      stressDescriptors: selectedStressDescriptors.length > 0 ? selectedStressDescriptors : null,
      feelings: selectedFeelings.length > 0 ? selectedFeelings : null,
      fatigueLevel: fatigueValue,
      fatigueType: selectedFatigueType.length > 0 ? selectedFatigueType : null,
      worryLevel: worryValue,
      feelingBlueLevel: feelingBlueValue,
      sleepQuality: sleepFatigueValue,
      bodySensations: selectedBodySensations.length > 0 ? selectedBodySensations : null,
      mood: selectedMoodChip ?? null,
      skippedSteps: [...skippedCheckinSteps],
    });
    const userCustomisation = currentUser?.avatarCustomisation ?? null;
    const characterId = userCustomisation?.characterId ?? null;
    if (characterId) {
      const newStreak = computeStreakFromEntries(updatedEntries);
      const category = getGuideCategory({ painLevel: painValue ?? 0, moodLevel: moodValue ?? 0, isVOC: checkinIsVOC === true, streak: newStreak });
      const msg = getGuideMessage(characterId, category, newStreak);
      setGuideAffirmation(msg);
    }
    clearCheckinDraft();
    setCheckinStep(1);
    setCheckinPainAreas({});
    setBodySpotWords({});
    setBodySpotFreeText({});
    setActiveBodySpot(null);
    setMindBodyStressValue(null);
    setGroundingShown(false);
    setMindBodyChips([]);
    setSelectedCopingStrategies([]);
    setCustomCopingStrategies([]);
    setCustomCopingInput('');
    setShowCustomCopingInput(false);
    setSelectedProtectiveFactors([]);
    setCheckinIsVOC(null);
    setSleepFatigueValue(null);
    setSelectedBodySensations([]);
    setStressValue(null);
    setSelectedStressDescriptors([]);
    setSelectedFeelings([]);
    setFatigueValue(null);
    setSelectedFatigueType([]);
    setWorryValue(null);
    setFeelingBlueValue(null);
    setSelectedMoodChip(null);
    setSkippedCheckinSteps([]);
    setShowCrisisReturnNudge(false);
    setShowAnchorNudge(false);
    if (!skipScreenTransition) {
      showScreenRef.current('checkin');
    }
  }

  // ── Handle open check-in ──────────────────────────────────────────

  function handleOpenCheckin(_date?: string) {
    startCheckin();
  }

  return {
    // Daily check-in form state
    checkinStep, setCheckinStep,
    checkinDone, setCheckinDone,
    watersCheck, setWatersCheck,
    selectedBodySensations, setSelectedBodySensations,
    stressValue, setStressValue,
    selectedStressDescriptors, setSelectedStressDescriptors,
    selectedFeelings, setSelectedFeelings,
    fatigueValue, setFatigueValue,
    selectedFatigueType, setSelectedFatigueType,
    worryValue, setWorryValue,
    selectedWorryChips, setSelectedWorryChips,
    feelingBlueValue, setFeelingBlueValue,
    selectedBlueChips, setSelectedBlueChips,
    selectedSleepChips, setSelectedSleepChips,
    selectedMoodChip, setSelectedMoodChip,
    customCheckinChips, setCustomCheckinChips,
    skippedCheckinSteps, setSkippedCheckinSteps,
    showCrisisReturnNudge, setShowCrisisReturnNudge,
    showAnchorNudge, setShowAnchorNudge,
    checkinSummary, setCheckinSummary,
    guideAffirmation, setGuideAffirmation,
    painValue, setPainValue,
    moodValue, setMoodValue,
    energyValue, setEnergyValue,
    checkinPainAreas, setCheckinPainAreas,
    bodySpotWords, setBodySpotWords,
    bodySpotFreeText, setBodySpotFreeText,
    activeBodySpot, setActiveBodySpot,
    painMapResult, setPainMapResult,
    emotionMapResult, setEmotionMapResult,
    mindBodyStressValue, setMindBodyStressValue,
    groundingShown, setGroundingShown,
    mindBodyChips, setMindBodyChips,
    sleepFatigueValue, setSleepFatigueValue,
    selectedCopingStrategies, setSelectedCopingStrategies,
    customCopingStrategies, setCustomCopingStrategies,
    customCopingInput, setCustomCopingInput,
    showCustomCopingInput, setShowCustomCopingInput,
    selectedTreatment, setSelectedTreatment,
    selectedProtectiveFactors, setSelectedProtectiveFactors,
    checkinIsVOC, setCheckinIsVOC,

    // Check-in entries
    checkinEntries,
    setCheckinEntries,
    savedCheckins, setSavedCheckins,
    lastCrisisDate, setLastCrisisDate,
    totalCheckins, setTotalCheckins,
    streak,

    // Check-in intro & draft
    showCheckinIntro, setShowCheckinIntro,
    showEDCheckinIntro, setShowEDCheckinIntro,
    checkinDraftActive, setCheckinDraftActive,
    checkinReturnScreen, setCheckinReturnScreen,

    // Scroll state
    checkinScrollTop, setCheckinScrollTop,
    checkinScrollBottom, setCheckinScrollBottom,
    checkinScreenRef,

    // Enabled questions
    enabledCheckinQuestions, setEnabledCheckinQuestions,
    toggleCheckinQuestion,

    // Body map metrics
    bodyMapMetrics,

    // Functions
    loadCheckins,
    saveCheckinDraft,
    clearCheckinDraft,
    exitCheckinWithSave,
    skipCheckinStep,
    nextCheckinStep,
    prevCheckinStep,
    toggleCopingStrategy,
    toggleProtectiveFactor,
    doStartCheckin,
    startCheckin,
    submitCheckin,
    handleOpenCheckin,
  };
}
